import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, Mail } from 'lucide-react';

// Twitter (X) SVG icon
function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// Instagram SVG icon
function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

const footerLinks = {
  Marketplace: [
    { label: 'Browse All Listings', href: '/listings-feed' },
    { label: 'Post a Listing', href: '/create-listing' },
    { label: 'Resources & Guides', href: '/resources' },
    { label: 'How It Works', href: '/how-it-works' },
  ],
  Categories: [
    { label: 'Crates & Kennels', href: '/listings-feed?cat=crates' },
    { label: 'Bite Equipment', href: '/listings-feed?cat=bite-equipment' },
    { label: 'Leads & Collars', href: '/listings-feed?cat=leads-collars' },
    { label: 'E-Collars', href: '/listings-feed?cat=ecollars' },
    { label: 'Training Equipment', href: '/listings-feed?cat=training' },
  ],
  Support: [
    { label: 'Safety & Trust', href: '/safety' },
    { label: 'Seller Guidelines', href: '/guidelines' },
    { label: 'Contact Us', href: '/contact' },
    { label: 'Report a Listing', href: '/report' },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-[hsl(222,47%,6%)] border-t border-[hsl(222,25%,18%)] mt-20">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-10">
          {/* Brand column */}
          <div className="xl:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <AppLogo size={32} />
              <span className="font-display text-lg text-white tracking-tight">
                Driven<span className="text-[hsl(28,85%,48%)]">Market</span>
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
              The UK's specialist marketplace for working dog, sport dog, and professional training equipment. Built for handlers who take it seriously.
            </p>
            <div className="flex items-center gap-2 mt-5">
              <Shield className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-slate-500">Verified sellers. Secure messaging. UK-based.</span>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all duration-150">
                <TwitterIcon className="w-4 h-4" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all duration-150">
                <InstagramIcon className="w-4 h-4" />
              </a>
              <a href="mailto:hello@drivenmarket.co.uk" className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all duration-150">
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks)?.map(([section, links]) => (
            <div key={`footer-section-${section?.toLowerCase()}`}>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">{section}</h4>
              <ul className="flex flex-col gap-2.5">
                {links?.map((link) => (
                  <li key={`footer-link-${link?.href}`}>
                    <Link href={link?.href} className="text-slate-400 hover:text-white text-sm transition-colors duration-150">
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-[hsl(222,25%,18%)] mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-600">© 2026 DrivenMarket Ltd. All rights reserved. Registered in England &amp; Wales.</p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link href="/privacy" className="text-xs text-slate-600 hover:text-slate-400 transition-colors duration-150">Privacy Policy</Link>
            <Link href="/terms" className="text-xs text-slate-600 hover:text-slate-400 transition-colors duration-150">Terms of Use</Link>
            <Link href="/cookies" className="text-xs text-slate-600 hover:text-slate-400 transition-colors duration-150">Cookies</Link>
            <Link href="/resources" className="text-xs text-slate-600 hover:text-slate-400 transition-colors duration-150">Resources</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}